
import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function StatistiquesPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>({});
  const [medecins, setMedecins] = useState<any[]>([]);
  const [rdvParMois, setRdvParMois] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('mois');

  useEffect(() => {
    if (user) {
      checkAdmin();
      loadStatistics();
    }
  }, [user]);

  const checkAdmin = async () => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    try {
      // Accepter super_admin et admin via le contexte
      if (role === 'super_admin' || role === 'admin') {
        return;
      }

      const { data, error } = await supabase
        .from('super_admins')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error || !data) {
        navigate('/', { replace: true });
        return;
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
      navigate('/', { replace: true });
    }
  };

  const loadStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      const [rdvResult, patientsResult, medecinsResult, prescriptionsResult, suivisResult] = await Promise.all([
        supabase
          .from('rendez_vous')
          .select(`
            *,
            medecins(nom, prenom, specialite),
            patients(nom, prenom)
          `)
          .order('date_rdv', { ascending: false }),
        
        supabase
          .from('patients')
          .select('*'),
        
        supabase
          .from('medecins')
          .select(`
            *,
            rendez_vous(id, statut, heure_debut_reel, heure_fin_reel)
          `),
        
        supabase
          .from('prescriptions')
          .select('*'),
        
        supabase
          .from('suivis_patients')
          .select('*')
      ]);

      if (rdvResult.error || patientsResult.error || medecinsResult.error || 
          prescriptionsResult.error || suivisResult.error) {
        throw new Error('Error fetching statistics data');
      }

      const rdvData = rdvResult.data || [];
      const patientsData = patientsResult.data || [];
      const medecinsData = medecinsResult.data || [];
      const prescriptionsData = prescriptionsResult.data || [];
      const suivisData = suivisResult.data || [];

      const rdvTermines = rdvData.filter(rdv => rdv.statut === 'termine');
      const rdvEnAttente = rdvData.filter(rdv => rdv.statut === 'en_attente');
      const rdvEnCours = rdvData.filter(rdv => rdv.statut === 'en_cours');
      const rdvAnnules = rdvData.filter(rdv => rdv.statut === 'annule');

      const today = new Date();
      const thisMonth = rdvData.filter(rdv => {
        const rdvDate = new Date(rdv.date_rdv);
        return rdvDate.getMonth() === today.getMonth() && 
               rdvDate.getFullYear() === today.getFullYear();
      });

      const lastMonth = rdvData.filter(rdv => {
        const rdvDate = new Date(rdv.date_rdv);
        const lastMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return rdvDate.getMonth() === lastMonthDate.getMonth() && 
               rdvDate.getFullYear() === lastMonthDate.getFullYear();
      });

      const evolutionRdv = thisMonth.length - lastMonth.length;
      const tauxEvolution = lastMonth.length > 0 
        ? ((evolutionRdv / lastMonth.length) * 100).toFixed(1)
        : '0';

      const medecinsWithStats = medecinsData.map(medecin => {
        const rdvs = medecin.rendez_vous || [];
        const rdvTermines = rdvs.filter((rdv: any) => rdv.statut === 'termine');
        
        let heuresTravail = 0;
        rdvTermines.forEach((rdv: any) => {
          if (rdv.heure_debut_reel && rdv.heure_fin_reel) {
            const today = new Date().toISOString().split('T')[0];
            const debut = new Date(`${today}T${rdv.heure_debut_reel}`);
            const fin = new Date(`${today}T${rdv.heure_fin_reel}`);
            if (!isNaN(debut.getTime()) && !isNaN(fin.getTime())) {
              heuresTravail += (fin.getTime() - debut.getTime()) / (1000 * 60 * 60);
            }
          }
        });

        return {
          ...medecin,
          nombreConsultations: rdvTermines.length,
          heuresTravail: Math.round(heuresTravail * 10) / 10,
        };
      });

      medecinsWithStats.sort((a, b) => b.nombreConsultations - a.nombreConsultations);

      const rdvParMoisData = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const rdvMois = rdvData.filter(rdv => {
          const rdvDate = new Date(rdv.date_rdv);
          return rdvDate.getMonth() === date.getMonth() && 
                 rdvDate.getFullYear() === date.getFullYear();
        });
        
        rdvParMoisData.push({
          mois: date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
          total: rdvMois.length,
          termines: rdvMois.filter(rdv => rdv.statut === 'termine').length,
          annules: rdvMois.filter(rdv => rdv.statut === 'annule').length,
        });
      }

      setStats({
        totalRdv: rdvData.length,
        rdvTermines: rdvTermines.length,
        rdvEnAttente: rdvEnAttente.length,
        rdvEnCours: rdvEnCours.length,
        rdvAnnules: rdvAnnules.length,
        totalPatients: patientsData.length,
        totalMedecins: medecinsData.length,
        totalPrescriptions: prescriptionsData.length,
        totalSuivis: suivisData.length,
        rdvCeMois: thisMonth.length,
        evolutionRdv: evolutionRdv,
        tauxEvolution: tauxEvolution,
        tauxReussite: rdvData.length > 0 
          ? ((rdvTermines.length / rdvData.length) * 100).toFixed(1)
          : '0',
        tauxAnnulation: rdvData.length > 0 
          ? ((rdvAnnules.length / rdvData.length) * 100).toFixed(1)
          : '0',
      });

      setMedecins(medecinsWithStats);
      setRdvParMois(rdvParMoisData);
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-clinic-blue-dark text-xl">Chargement des statistiques...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">{error}</div>
          <button 
            onClick={loadStatistics}
            className="px-4 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-clinic-blue-dark">Statistiques &amp; Rapports</h1>
              <p className="text-gray-600 mt-2">Analyse détaillée de l'activité de la clinique</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap"
            >
              ← Retour au tableau de bord
            </button>
          </div>
        </div>

        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-calendar-check-line text-2xl text-blue-600"></i>
              </div>
              <span className={`text-sm font-semibold ${
                parseFloat(stats.tauxEvolution || '0') >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {parseFloat(stats.tauxEvolution || '0') >= 0 ? '+' : ''}{stats.tauxEvolution}%
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.totalRdv || 0}</div>
            <div className="text-sm text-gray-600">Total rendez-vous</div>
            <div className="text-xs text-gray-500 mt-2">{stats.rdvCeMois || 0} ce mois</div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-green-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-check-double-line text-2xl text-green-600"></i>
              </div>
              <span className="text-sm font-semibold text-green-600">{stats.tauxReussite}%</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.rdvTermines || 0}</div>
            <div className="text-sm text-gray-600">Consultations terminées</div>
            <div className="text-xs text-gray-500 mt-2">Taux de réussite</div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-purple-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-group-line text-2xl text-purple-600"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.totalPatients || 0}</div>
            <div className="text-sm text-gray-600">Patients inscrits</div>
            <div className="text-xs text-gray-500 mt-2">{stats.totalMedecins || 0} médecins actifs</div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-file-text-line text-2xl text-orange-600"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.totalPrescriptions || 0}</div>
            <div className="text-sm text-gray-600">Prescriptions émises</div>
            <div className="text-xs text-gray-500 mt-2">{stats.totalSuivis || 0} suivis médicaux</div>
          </div>
        </div>

        {/* Graphique évolution */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border-2 border-gray-100">
          <h2 className="text-xl font-bold text-clinic-blue-dark mb-6">Évolution des rendez-vous (6 derniers mois)</h2>
          <div className="space-y-4">
            {rdvParMois.map((mois, index) => {
              const maxValue = Math.max(...rdvParMois.map(m => m.total));
              const percentage = maxValue > 0 ? (mois.total / maxValue) * 100 : 0;
              
              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{mois.mois}</span>
                    <span className="text-sm font-bold text-clinic-blue-dark">{mois.total} RDV</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-clinic-blue to-clinic-blue-dark h-full rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    >
                      <span className="text-xs text-white font-semibold">
                        {mois.termines} terminés, {mois.annules} annulés
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top médecins */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-100">
          <h2 className="text-xl font-bold text-clinic-blue-dark mb-6">Top Médecins par Consultations</h2>
          <div className="space-y-4">
            {medecins.slice(0, 10).map((medecin, index) => (
              <div key={medecin.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                  index === 0 ? 'bg-yellow-500' :
                  index === 1 ? 'bg-gray-400' :
                  index === 2 ? 'bg-orange-600' :
                  'bg-clinic-blue'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-clinic-blue-dark">
                    Dr {medecin.nom} {medecin.prenom}
                  </div>
                  <div className="text-sm text-gray-600">{medecin.specialite}</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-clinic-blue-dark">{medecin.nombreConsultations || 0}</div>
                  <div className="text-xs text-gray-500">{medecin.heuresTravail || 0}h travail</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Statistiques détaillées */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-yellow-100">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.rdvEnAttente || 0}</div>
                <div className="text-sm text-gray-600">En attente</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-orange-100">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-loader-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.rdvEnCours || 0}</div>
                <div className="text-sm text-gray-600">En cours</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-red-100">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <i className="ri-close-circle-line text-2xl text-red-600"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.rdvAnnules || 0}</div>
                <div className="text-sm text-gray-600">Annulés ({stats.tauxAnnulation}%)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
